import { AuthError, verifyTOTPSetup } from "aws-amplify/auth";
import { motion } from "framer-motion";
import { AlertCircle, Copy } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { useState } from "react";
import { useForm } from "react-hook-form";

import { Card } from "@/components/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { copyToClipboard } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";

import { Totp2FAFormData, totp2FASchema } from "../types/totp-2fa.schema";

type Totp2FAProps = {
  secret: string;
  setupUri: string;
  goBack: () => void;
  onSuccess: () => Promise<void>;
};

export function Totp2FAForm({
  secret,
  setupUri,
  goBack,
  onSuccess,
}: Totp2FAProps) {
  const form = useForm<Totp2FAFormData>({
    resolver: zodResolver(totp2FASchema),
    defaultValues: { code: "", hash: secret },
  });
  const [showHash, setShowHash] = useState(false);

  const handleSubmit = async (data: Totp2FAFormData) => {
    try {
      await verifyTOTPSetup({ code: data.code });
      await onSuccess();
    } catch (error) {
      if (error instanceof AuthError) {
        switch (error.name) {
          case "RespondToAuthChallengeException":
            form.setError("code", {
              type: "manual",
              message: "The code you entered is incorrect. Please try again.",
            });
            break;
          default:
            form.setError("root", {
              type: "manual",
              message: "The code you entered is incorrect. Please try again.",
            });
        }
      } else {
        console.error("Unexpected error:", error);
        form.setError("root", {
          type: "manual",
          message: "The code you entered is incorrect. Please try again.",
        });
      }
    }
  };

  return (
    <Card>
      <Card.Header
        title="Setup 2FA"
        description="Select your preferred method"
      />
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <Card.Content>
            <p className="text-md mb-6 font-normal text-secondary">
              Scan the QR Code below on your authentication app and enter the
              code generated to continue. Alternatively copy the hash code and
              paste into your authentication app.
            </p>

            <QRCodeCanvas
              value={setupUri}
              className="mb-2 dark:border-4 dark:border-white"
              size={256}
            />
            <Button
              variant="link"
              type="button"
              className="px-0"
              onClick={() => setShowHash(!showHash)}
            >
              {showHash ? "Hide Hash Code" : "Reveal Hash Code"}
            </Button>
            <motion.div
              className="overflow-hidden"
              initial={{ opacity: 0, height: 0 }}
              animate={
                showHash
                  ? { opacity: 1, height: "auto" } // 224px is equivalent to h-56
                  : { opacity: 0, height: 0 }
              }
              transition={{ duration: 0.3 }}
            >
              <TooltipProvider>
                <FormField
                  control={form.control}
                  name="hash"
                  render={({ field }) => (
                    <FormItem className="w-1/2">
                      <FormLabel>Hash Code</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            disabled
                            autoComplete="off"
                            className="truncate pr-10"
                            {...field}
                          />
                          <Tooltip delayDuration={0}>
                            <TooltipTrigger asChild>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                                onClick={() => copyToClipboard(field.value)}
                              >
                                <Copy className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Copy to clipboard</p>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </TooltipProvider>
            </motion.div>
            <FormField
              control={form.control}
              name="code"
              render={({ field }) => (
                <FormItem className="mt-6">
                  <FormLabel isRequired>Enter Code</FormLabel>
                  <FormControl>
                    <InputOTP
                      maxLength={6}
                      {...field}
                      disabled={form.formState.isSubmitting}
                      onComplete={(code) => {
                        form.setValue("code", code);
                        form.handleSubmit(handleSubmit)();
                      }}
                      autoFocus
                      autoComplete="one-time-code"
                    >
                      <InputOTPGroup>
                        <InputOTPSlot index={0} />
                        <InputOTPSlot index={1} />
                        <InputOTPSlot index={2} />
                        <InputOTPSlot index={3} />
                        <InputOTPSlot index={4} />
                        <InputOTPSlot index={5} />
                      </InputOTPGroup>
                    </InputOTP>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {form.formState.errors.root && (
              <Alert variant="destructive" className="mt-6">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Unexpected error</AlertTitle>
                <AlertDescription>
                  {form.formState.errors.root.message}
                </AlertDescription>
              </Alert>
            )}
          </Card.Content>
          <Card.Footer className="flex items-center justify-between">
            <p className="text-sm text-secondary">Step 2 of 2</p>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={goBack}>
                Back
              </Button>
              <Button type="submit" disabled={!form.formState.isValid}>
                Verify
              </Button>
            </div>
          </Card.Footer>
        </form>
      </Form>
    </Card>
  );
}
